import { AvatarView } from "@/components/AvatarView";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useI18n } from "@/lib/i18n";
import { Lock, Zap, Check } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#00E5FF", "#FF007F", "#00C853", "#AA00FF", "#FF6D00", "#FFD600", "#2962FF", "#FF3333"];

function ItemCard({ item, kind, selected, unlocked, points, onEquip, onPurchase, testid }) {
  const cost = Number(item.cost || 0);
  const isFree = cost <= 0;
  const owned = isFree || (unlocked || []).includes(item.id);
  const canBuy = points >= cost;
  const handle = async () => {
    if (owned) return onEquip();
    if (!canBuy) return toast.error(`Necesitas ${cost} puntos (tienes ${points})`);
    if (!window.confirm(`¿Desbloquear «${item.name || "elemento"}» por ${cost} puntos?`)) return;
    const ok = await onPurchase(item, kind);
    if (ok) onEquip();
  };
  return (
    <button data-testid={testid} onClick={handle}
      className={`relative w-24 rounded-2xl border-2 p-2 flex flex-col items-center gap-1 transition-transform hover:scale-105 ${selected ? "border-primary bg-accent" : "border-border"}`}>
      <div className="relative w-16 h-16">
        <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
        {!owned && <div className="absolute inset-0 rounded-lg bg-black/45 flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></div>}
        {selected && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center"><Check className="w-3 h-3" /></span>}
      </div>
      <span className="text-[11px] font-semibold truncate w-full text-center">{item.name || "—"}</span>
      {owned ? <span className="text-[10px] text-emerald-500 font-bold">✓</span>
        : <span className="text-[10px] font-bold flex items-center gap-0.5 text-primary"><Zap className="w-3 h-3" />{cost}</span>}
    </button>
  );
}

function SlotGrid({ items, kind, selectedImage, cz, set, slot, unlocked, points, onPurchase, empty }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground py-4">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const selected = selectedImage === it.image;
        return (
          <ItemCard key={it.id} item={it} kind={kind} selected={selected} unlocked={unlocked} points={points}
            testid={`pick-${slot}-${it.id}`}
            onEquip={() => set({ [slot]: selected ? null : it.image })}
            onPurchase={onPurchase} />
        );
      })}
    </div>
  );
}

export function AvatarCustomizer({ assets, accessories, value, onChange, previewStudent, onPurchase }) {
  const { t } = useI18n();
  const cz = value || {};
  const set = (patch) => onChange({ ...cz, ...patch });
  const points = previewStudent?.points || 0;
  const unlocked = previewStudent?.unlocked || [];

  const perso = accessories.filter((a) => a.category === "personalizacion");
  const equip = accessories.filter((a) => a.category === "equipamiento");
  const marcos = accessories.filter((a) => a.category === "marco");
  const banners = accessories.filter((a) => a.category === "banner");

  const purchase = onPurchase || (async () => true);

  return (
    <div className="grid sm:grid-cols-[auto_1fr] gap-6 items-start">
      <div className="flex flex-col items-center gap-3">
        <div className="rounded-2xl p-3 w-fit" style={{ background: cz.banner_color ? cz.banner_color + "33" : "hsl(var(--muted))" }}>
          <AvatarView student={{ ...previewStudent, customization: cz }} size={150} />
        </div>
        <span className="px-3 h-8 rounded-full bg-primary/15 text-primary font-extrabold flex items-center gap-1.5 text-sm" data-testid="cz-points"><Zap className="w-4 h-4" /> {points} pts</span>
      </div>
      <Tabs defaultValue="base">
        <TabsList className="rounded-full bg-muted flex flex-wrap h-auto p-1">
          <TabsTrigger value="base" data-testid="cz-tab-base" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-card">◉ {t("czBase")}</TabsTrigger>
          <TabsTrigger value="perso" data-testid="cz-tab-perso" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-card">✦ {t("czPerso")}</TabsTrigger>
          <TabsTrigger value="equip" data-testid="cz-tab-equip" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-card">❖ {t("czExtras")}</TabsTrigger>
          <TabsTrigger value="estilo" data-testid="cz-tab-estilo" className="rounded-full px-4 py-2 font-bold data-[state=active]:bg-card">✎ {t("czStyle")}</TabsTrigger>
        </TabsList>
        <div className="mt-4 space-y-5">
          <TabsContent value="base" className="space-y-4">
            <SlotGrid items={assets} kind="asset" selectedImage={cz.base_image || previewStudent?.avatar} cz={cz} set={set} slot="base_image" unlocked={unlocked} points={points} onPurchase={purchase} empty={t("noItems")} />
          </TabsContent>
          <TabsContent value="perso">
            <SlotGrid items={perso} kind="accessory" selectedImage={cz.perso_image} cz={cz} set={set} slot="perso_image" unlocked={unlocked} points={points} onPurchase={purchase} empty={t("noItems")} />
          </TabsContent>
          <TabsContent value="equip">
            <SlotGrid items={equip} kind="accessory" selectedImage={cz.equip_image} cz={cz} set={set} slot="equip_image" unlocked={unlocked} points={points} onPurchase={purchase} empty={t("noItems")} />
          </TabsContent>
          <TabsContent value="estilo" className="space-y-5">
            <div><div className="text-sm font-bold mb-2">{t("frameColor")}</div><div className="flex gap-2 flex-wrap"><button onClick={() => set({ frame_color: null })} className="px-3 h-9 rounded-full text-xs font-bold bg-muted">{t("none")}</button>{COLORS.map((c) => <button key={c} onClick={() => set({ frame_color: c })} className="w-9 h-9 rounded-full" style={{ background: c, outline: cz.frame_color === c ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}</div></div>
            <div><div className="text-sm font-bold mb-2">{t("bannerColor")}</div><div className="flex gap-2 flex-wrap"><button onClick={() => set({ banner_color: null })} className="px-3 h-9 rounded-full text-xs font-bold bg-muted">{t("none")}</button>{COLORS.map((c) => <button key={c} onClick={() => set({ banner_color: c })} className="w-9 h-9 rounded-full" style={{ background: c, outline: cz.banner_color === c ? "3px solid hsl(var(--foreground))" : "none", outlineOffset: 2 }} />)}</div></div>
            {marcos.length > 0 && <div><div className="text-sm font-bold mb-2">{t("frames")}</div><SlotGrid items={marcos} kind="accessory" selectedImage={cz.frame_image} cz={cz} set={set} slot="frame_image" unlocked={unlocked} points={points} onPurchase={purchase} empty="" /></div>}
            {banners.length > 0 && <div><div className="text-sm font-bold mb-2">{t("banners")}</div><SlotGrid items={banners} kind="accessory" selectedImage={cz.banner_image} cz={cz} set={set} slot="banner_image" unlocked={unlocked} points={points} onPurchase={purchase} empty="" /></div>}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
